import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Clock, Plus, Edit2, Trash2, GripVertical, Sparkles, Save, Download, Share2, ChevronDown, ChevronUp, Music, Camera, Utensils, Wine, Mic, Heart as HeartIcon, Users, Car, Church, PartyPopper, StickyNote, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import VALAssistant from '../components/VALAssistant';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const Timeline = ({ user }) => {
  const navigate = useNavigate();
  const [weddingPlan, setWeddingPlan] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVAL, setShowVAL] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingEventData, setEditingEventData] = useState(null); // Local state for editing
  const [expandedSection, setExpandedSection] = useState('ceremony');
  const [hasCustomTimeline, setHasCustomTimeline] = useState(false);
  
  // Notes modal state
  const [notesModal, setNotesModal] = useState({ open: false, event: null });
  const [noteText, setNoteText] = useState('');

  // Event categories with icons
  const eventCategories = [
    { id: 'prep', name: 'Getting Ready', icon: '💄', color: 'pink' },
    { id: 'ceremony', name: 'Ceremony', icon: '💒', color: 'rose' },
    { id: 'cocktail', name: 'Cocktail Hour', icon: '🥂', color: 'amber' },
    { id: 'reception', name: 'Reception', icon: '🎉', color: 'purple' },
    { id: 'dinner', name: 'Dinner & Toasts', icon: '🍽️', color: 'emerald' },
    { id: 'dancing', name: 'Dancing & Fun', icon: '💃', color: 'blue' },
    { id: 'special', name: 'Special Moments', icon: '✨', color: 'yellow' },
    { id: 'closing', name: 'Closing', icon: '🌟', color: 'indigo' },
  ];

  // Default timeline template
  const defaultTimeline = [
    // Getting Ready
    { id: '1', category: 'prep', time: '10:00 AM', duration: 120, title: 'Hair & Makeup Begins', description: 'Bride and bridesmaids start hair and makeup', vendor: 'Hair & Makeup Artist', notes: '' },
    { id: 'snack', category: 'prep', time: '12:00 PM', duration: 30, title: 'Snack Break', description: 'Remember to eat something before things get started!', vendor: '', notes: 'Don\'t skip this - you\'ll thank yourself later' },
    { id: '2', category: 'prep', time: '12:00 PM', duration: 60, title: 'Groom & Groomsmen Get Ready', description: 'Groom party dresses and prepares', vendor: '', notes: '' },
    { id: '3', category: 'prep', time: '01:00 PM', duration: 30, title: 'First Look / Private Moment', description: 'Optional private first look between couple', vendor: 'Photographer', notes: '' },
    { id: '4', category: 'prep', time: '01:30 PM', duration: 60, title: 'Wedding Party Photos', description: 'Bridal party and family portraits', vendor: 'Photographer', notes: '' },
    
    // Ceremony
    { id: '5', category: 'ceremony', time: '02:30 PM', duration: 15, title: 'Guests Begin Arriving', description: 'Ushers seat guests as they arrive', vendor: '', notes: '' },
    { id: '6', category: 'ceremony', time: '02:45 PM', duration: 15, title: 'Seating Music Begins', description: 'DJ/musicians play prelude music', vendor: 'DJ/Musicians', notes: '' },
    { id: '7', category: 'ceremony', time: '03:00 PM', duration: 5, title: 'Family Seating', description: 'Mothers and grandparents are seated', vendor: '', notes: '' },
    { id: '8', category: 'ceremony', time: '03:05 PM', duration: 5, title: 'Wedding Party Processional', description: 'Bridesmaids and groomsmen walk down aisle', vendor: '', notes: '' },
    { id: '9', category: 'ceremony', time: '03:10 PM', duration: 5, title: 'Bride Processional', description: 'Bride walks down the aisle', vendor: '', notes: 'Cue music change' },
    { id: '10', category: 'ceremony', time: '03:15 PM', duration: 30, title: 'Wedding Ceremony', description: 'Vows, rings, unity ceremony', vendor: 'Officiant', notes: '' },
    { id: '11', category: 'ceremony', time: '03:45 PM', duration: 5, title: 'Recessional & Kiss', description: 'First kiss and couple exits', vendor: '', notes: '' },
    
    // Cocktail Hour
    { id: '12', category: 'cocktail', time: '04:00 PM', duration: 60, title: 'Cocktail Hour Begins', description: 'Guests enjoy drinks and appetizers', vendor: 'Caterer/Bartender', notes: '' },
    { id: '13', category: 'cocktail', time: '04:00 PM', duration: 45, title: 'Couple Photos', description: 'Bride and groom portraits during cocktail hour', vendor: 'Photographer', notes: '' },
    
    // Reception
    { id: '14', category: 'reception', time: '05:00 PM', duration: 5, title: 'Guests Invited to Reception', description: 'MC announces reception is open', vendor: 'DJ/MC', notes: '' },
    { id: '15', category: 'reception', time: '05:05 PM', duration: 10, title: 'Wedding Party Introductions', description: 'Bridal party announced and enters', vendor: 'DJ/MC', notes: '' },
    { id: '16', category: 'reception', time: '05:15 PM', duration: 5, title: 'Newlyweds Grand Entrance', description: 'Couple announced for first time as married', vendor: 'DJ/MC', notes: '' },
    { id: '17', category: 'reception', time: '05:20 PM', duration: 5, title: 'First Dance', description: 'Couple\'s first dance as newlyweds', vendor: 'DJ', notes: '' },
    
    // Dinner & Toasts
    { id: '18', category: 'dinner', time: '05:30 PM', duration: 5, title: 'Welcome & Blessing', description: 'Welcome speech and/or blessing before meal', vendor: '', notes: '' },
    { id: '19', category: 'dinner', time: '05:35 PM', duration: 60, title: 'Dinner Service', description: 'Main meal is served', vendor: 'Caterer', notes: '' },
    { id: '20', category: 'dinner', time: '06:15 PM', duration: 10, title: 'Best Man Toast', description: 'Best man gives speech', vendor: '', notes: '' },
    { id: '21', category: 'dinner', time: '06:25 PM', duration: 10, title: 'Maid of Honor Toast', description: 'Maid of honor gives speech', vendor: '', notes: '' },
    
    // Dancing & Special Moments
    { id: '22', category: 'dancing', time: '06:45 PM', duration: 5, title: 'Parent Dances', description: 'Father-daughter and mother-son dances', vendor: 'DJ', notes: '' },
    { id: '23', category: 'dancing', time: '06:50 PM', duration: 90, title: 'Open Dancing', description: 'Dance floor opens to all guests', vendor: 'DJ', notes: '' },
    { id: '24', category: 'special', time: '07:30 PM', duration: 15, title: 'Cake Cutting', description: 'Couple cuts the wedding cake', vendor: 'Photographer', notes: '' },
    { id: '25', category: 'special', time: '08:00 PM', duration: 20, title: 'Bouquet & Garter Toss', description: 'Traditional tosses (optional)', vendor: 'DJ', notes: '' },
    { id: '26', category: 'special', time: '08:30 PM', duration: 15, title: 'The Shoe Game', description: 'Fun game for guests to enjoy', vendor: 'DJ/MC', notes: '' },
    
    // Closing
    { id: '27', category: 'closing', time: '09:30 PM', duration: 5, title: 'Last Dance', description: 'Final dance of the evening', vendor: 'DJ', notes: '' },
    { id: '28', category: 'closing', time: '09:35 PM', duration: 10, title: 'Grand Exit', description: 'Sparkler/bubble send-off for couple', vendor: 'Coordinator', notes: '' },
    { id: '29', category: 'closing', time: '10:00 PM', duration: 0, title: 'Venue Hard Out', description: 'All guests and vendors must exit', vendor: '', notes: 'Ensure cleanup is complete' },
  ];

  const [newEvent, setNewEvent] = useState({
    category: 'ceremony',
    time: '',
    duration: 30,
    title: '',
    description: '',
    vendor: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [user.id]);

  const loadData = async () => {
    try {
      const [planRes, timelineRes] = await Promise.all([
        axios.get(`${API}/wedding-plans/${user.id}`),
        axios.get(`${API}/day-timeline/${user.id}`)
      ]);
      setWeddingPlan(planRes.data);
      
      if (timelineRes.data && timelineRes.data.length > 0) {
        setTimelineEvents(timelineRes.data);
        setHasCustomTimeline(true);
      } else {
        // Use default template if no timeline exists
        setTimelineEvents(defaultTimeline);
        setHasCustomTimeline(false);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setTimelineEvents(defaultTimeline);
    } finally {
      setLoading(false);
    }
  };

  const saveTimeline = async () => {
    try {
      await axios.post(`${API}/day-timeline/${user.id}`, { events: timelineEvents });
      toast.success('Timeline saved!');
    } catch (error) {
      toast.error('Failed to save timeline');
    }
  };

  const handleAddEvent = (e) => {
    e.preventDefault();
    if (!newEvent.title || !newEvent.time) {
      toast.error('Please enter a title and time');
      return;
    }

    const event = {
      id: Date.now().toString(),
      ...newEvent
    };

    // Insert in correct time order
    const updatedEvents = [...timelineEvents, event].sort((a, b) => {
      const timeA = convertTo24Hour(a.time);
      const timeB = convertTo24Hour(b.time);
      return timeA.localeCompare(timeB);
    });

    setTimelineEvents(updatedEvents);
    setNewEvent({
      category: 'ceremony',
      time: '',
      duration: 30,
      title: '',
      description: '',
      vendor: '',
      notes: ''
    });
    setShowAddEvent(false);
    toast.success('Event added to timeline!');
  };

  const handleDeleteEvent = (eventId) => {
    setTimelineEvents(timelineEvents.filter(e => e.id !== eventId));
    toast.success('Event removed');
  };

  const handleUpdateEvent = (eventId, updates) => {
    // Update local editing state only (don't close edit mode)
    setEditingEventData(prev => ({ ...prev, ...updates }));
  };

  // Start editing an event
  const startEditingEvent = (event) => {
    setEditingEvent(event.id);
    setEditingEventData({ ...event }); // Copy event data to local state
  };

  // Cancel editing without saving
  const cancelEditingEvent = () => {
    setEditingEvent(null);
    setEditingEventData(null);
  };

  // Save the edited event
  const saveEditedEvent = async () => {
    if (!editingEventData) return;
    
    // Update local state
    setTimelineEvents(timelineEvents.map(e => 
      e.id === editingEventData.id ? editingEventData : e
    ));
    
    // Save to backend
    try {
      await axios.put(`${API}/day-timeline/${user.id}/event/${editingEventData.id}`, editingEventData);
      toast.success('Event updated!');
    } catch (error) {
      console.error('Failed to save event:', error);
      // Still keep local changes even if backend fails
    }
    
    // Close edit mode
    setEditingEvent(null);
    setEditingEventData(null);
  };

  // Open notes modal for an event
  const openNotesModal = (event) => {
    setNotesModal({ open: true, event });
    setNoteText(event.notes || '');
  };

  // Close notes modal
  const closeNotesModal = () => {
    setNotesModal({ open: false, event: null });
    setNoteText('');
  };

  // Save note for an event
  const saveNote = async () => {
    if (!notesModal.event) return;
    
    const eventId = notesModal.event.id;
    
    // Update local state immediately
    setTimelineEvents(timelineEvents.map(e => 
      e.id === eventId ? { ...e, notes: noteText } : e
    ));
    
    // Save to backend
    try {
      await axios.put(`${API}/day-timeline/${user.id}/event-note`, {
        event_id: eventId,
        notes: noteText
      });
      toast.success('Note saved!');
    } catch (error) {
      console.error('Error saving note:', error);
      // Note is still saved locally even if API fails
    }
    
    closeNotesModal();
  };

  const convertTo24Hour = (time12h) => {
    if (!time12h) return '00:00';
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    if (hours === '12') hours = '00';
    if (modifier === 'PM') hours = parseInt(hours, 10) + 12;
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
  };

  // Group events by category
  const eventsByCategory = timelineEvents.reduce((acc, event) => {
    if (!acc[event.category]) acc[event.category] = [];
    acc[event.category].push(event);
    return acc;
  }, {});

  const getCategoryInfo = (categoryId) => {
    return eventCategories.find(c => c.id === categoryId) || eventCategories[0];
  };

  // Calculate total timeline duration
  const getTimelineRange = () => {
    if (timelineEvents.length === 0) return 'No events yet';
    const times = timelineEvents.map(e => e.time).filter(Boolean);
    if (times.length === 0) return 'No times set';
    const sorted = times.sort((a, b) => convertTo24Hour(a).localeCompare(convertTo24Hour(b)));
    return `${sorted[0]} - ${sorted[sorted.length - 1]}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-xl border-b border-stone-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#57534E] hover:text-[#881337]"
          >
            <ArrowLeft className="mr-2" size={18} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={24} fill="#881337" />
            <span className="text-xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Clock className="text-[#881337]" size={32} />
                <h1 className="text-4xl font-serif font-light tracking-tight text-[#1C1917]">
                  Day-Of Timeline
                </h1>
              </div>
              <p className="text-[#57534E] text-lg">
                Your wedding day, minute by minute
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowVAL(true)}
                className="rounded-full bg-gradient-to-r from-[#881337] to-[#9F1239] text-white"
              >
                <Sparkles size={18} className="mr-2" />
                Build with Grace
              </Button>
            </div>
          </div>

          {/* Timeline Overview */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm text-[#A8A29E]">Wedding Date</p>
                <p className="text-xl font-serif text-[#1C1917]">
                  {weddingPlan?.wedding_date 
                    ? (() => {
                        const [year, month, day] = weddingPlan.wedding_date.split('-').map(Number);
                        return new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                      })()
                    : 'Not set'
                  }
                </p>
              </div>
              <div>
                <p className="text-sm text-[#A8A29E]">Timeline Range</p>
                <p className="text-xl font-serif text-[#881337]">{getTimelineRange()}</p>
              </div>
              <div>
                <p className="text-sm text-[#A8A29E]">Total Events</p>
                <p className="text-xl font-serif text-[#1C1917]">{timelineEvents.length}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={saveTimeline} variant="outline" className="rounded-full">
                  <Save size={16} className="mr-2" />
                  Save
                </Button>
                <Button variant="outline" className="rounded-full">
                  <Download size={16} className="mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Add Event Button */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-sm text-[#57534E]">
            Click on any section to expand and edit events
          </p>
          <Button
            onClick={() => setShowAddEvent(true)}
            className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
          >
            <Plus size={18} className="mr-2" />
            Add Event
          </Button>
        </div>

        {/* Add Event Form */}
        {showAddEvent && (
          <div className="bg-white rounded-2xl p-6 border border-stone-200 mb-8">
            <h3 className="text-lg font-serif mb-4">Add Timeline Event</h3>
            <form onSubmit={handleAddEvent} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Time *</label>
                  <Input
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                    placeholder="e.g., 3:00 PM"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Duration (minutes)</label>
                  <Input
                    type="number"
                    value={newEvent.duration}
                    onChange={(e) => setNewEvent({ ...newEvent, duration: parseInt(e.target.value) || 0 })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Category</label>
                  <select
                    value={newEvent.category}
                    onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })}
                    className="w-full h-10 rounded-xl border border-stone-200 px-3"
                  >
                    {eventCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Event Title *</label>
                  <Input
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    placeholder="e.g., First Dance"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Vendor Responsible</label>
                  <Input
                    value={newEvent.vendor}
                    onChange={(e) => setNewEvent({ ...newEvent, vendor: e.target.value })}
                    placeholder="e.g., DJ"
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Description</label>
                <Textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  placeholder="Details about this event..."
                  className="rounded-xl"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowAddEvent(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white">
                  Add Event
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Timeline by Category */}
        <div className="space-y-4">
          {eventCategories.map(category => {
            const categoryEvents = eventsByCategory[category.id] || [];
            const isExpanded = expandedSection === category.id;
            
            return (
              <div key={category.id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => setExpandedSection(isExpanded ? null : category.id)}
                  className="w-full p-5 flex items-center justify-between hover:bg-stone-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{category.icon}</span>
                    <div className="text-left">
                      <h3 className="text-lg font-serif text-[#1C1917]">{category.name}</h3>
                      <p className="text-sm text-[#57534E]">
                        {categoryEvents.length} event{categoryEvents.length !== 1 ? 's' : ''}
                        {categoryEvents.length > 0 && ` • ${categoryEvents[0].time} - ${categoryEvents[categoryEvents.length - 1].time}`}
                      </p>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp size={24} className="text-stone-400" /> : <ChevronDown size={24} className="text-stone-400" />}
                </button>

                {/* Expanded Events */}
                {isExpanded && (
                  <div className="border-t border-stone-100">
                    {categoryEvents.length === 0 ? (
                      <div className="p-6 text-center text-[#57534E]">
                        No events in this section yet
                      </div>
                    ) : (
                      <div className="divide-y divide-stone-50">
                        {categoryEvents.map((event, idx) => (
                          <div key={event.id} className="p-4 hover:bg-stone-50 transition-colors">
                            {editingEvent === event.id && editingEventData ? (
                              // Edit Mode
                              <div className="space-y-3 bg-stone-50 p-4 rounded-xl border border-stone-200">
                                <div className="grid grid-cols-3 gap-3">
                                  <div>
                                    <label className="text-xs text-stone-500 mb-1 block">Time</label>
                                    <Input
                                      value={editingEventData.time}
                                      onChange={(e) => handleUpdateEvent(event.id, { time: e.target.value })}
                                      placeholder="e.g., 3:00 PM"
                                      className="rounded-lg text-sm"
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <label className="text-xs text-stone-500 mb-1 block">Title</label>
                                    <Input
                                      value={editingEventData.title}
                                      onChange={(e) => handleUpdateEvent(event.id, { title: e.target.value })}
                                      placeholder="Event title"
                                      className="rounded-lg text-sm"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs text-stone-500 mb-1 block">Duration (minutes)</label>
                                    <Input
                                      type="number"
                                      value={editingEventData.duration || ''}
                                      onChange={(e) => handleUpdateEvent(event.id, { duration: parseInt(e.target.value) || 0 })}
                                      placeholder="e.g., 30"
                                      className="rounded-lg text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-stone-500 mb-1 block">Vendor</label>
                                    <Input
                                      value={editingEventData.vendor || ''}
                                      onChange={(e) => handleUpdateEvent(event.id, { vendor: e.target.value })}
                                      placeholder="e.g., DJ"
                                      className="rounded-lg text-sm"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs text-stone-500 mb-1 block">Description</label>
                                  <Input
                                    value={editingEventData.description || ''}
                                    onChange={(e) => handleUpdateEvent(event.id, { description: e.target.value })}
                                    placeholder="Details about this event..."
                                    className="rounded-lg text-sm"
                                  />
                                </div>
                                <div className="flex justify-end gap-2 pt-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={cancelEditingEvent} 
                                    className="rounded-full text-xs"
                                  >
                                    Cancel
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    onClick={saveEditedEvent} 
                                    className="rounded-full text-xs bg-[#881337] hover:bg-[#9F1239] text-white"
                                  >
                                    Save Changes
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              // View Mode
                              <div className="flex items-start gap-4">
                                <div className="w-20 flex-shrink-0">
                                  <p className="font-mono text-sm font-semibold text-[#881337]">{event.time}</p>
                                  {event.duration > 0 && (
                                    <p className="text-xs text-[#A8A29E]">{event.duration} min</p>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-[#1C1917]">{event.title}</p>
                                  {event.description && (
                                    <p className="text-sm text-[#57534E] mt-1">{event.description}</p>
                                  )}
                                  {event.vendor && (
                                    <p className="text-xs text-[#881337] mt-1">📋 {event.vendor}</p>
                                  )}
                                  {event.notes && (
                                    <button 
                                      onClick={() => openNotesModal(event)}
                                      className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 hover:bg-amber-100 transition-colors"
                                    >
                                      <StickyNote size={12} />
                                      {event.notes}
                                    </button>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  {/* Add Note Button */}
                                  <button
                                    onClick={() => openNotesModal(event)}
                                    className="p-2 text-stone-400 hover:text-amber-500 transition-colors"
                                    title={event.notes ? "Edit note" : "Add note"}
                                  >
                                    <Plus size={16} />
                                  </button>
                                  <button
                                    onClick={() => startEditingEvent(event)}
                                    className="p-2 text-stone-400 hover:text-[#881337] transition-colors"
                                    title="Edit event"
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteEvent(event.id)}
                                    className="p-2 text-stone-400 hover:text-red-500 transition-colors"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Grace CTA */}
        <div className="mt-12 bg-gradient-to-br from-[#881337] to-[#9F1239] rounded-3xl p-8 text-white text-center">
          <Sparkles className="mx-auto mb-4" size={32} />
          <h3 className="text-2xl font-serif mb-2">Build Your Perfect Timeline with Grace</h3>
          <p className="text-rose-100 mb-4 max-w-2xl mx-auto">
            Let Grace help you create a realistic, stress-free timeline for your big day. 
            We&apos;ll walk through it together, one step at a time.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-sm text-rose-200 mb-6">
            <span className="bg-white/10 px-3 py-1 rounded-full">📸 Photo-optimized</span>
            <span className="bg-white/10 px-3 py-1 rounded-full">🌅 Sunset timing</span>
            <span className="bg-white/10 px-3 py-1 rounded-full">🍽️ Dinner flow</span>
            <span className="bg-white/10 px-3 py-1 rounded-full">💃 Special moments</span>
          </div>
          <Button
            onClick={() => setShowVAL(true)}
            className="bg-white text-[#881337] hover:bg-rose-50 rounded-full px-8"
          >
            <Sparkles size={18} className="mr-2" />
            Build My Timeline with Grace
          </Button>
        </div>
      </div>

      {/* Grace Assistant - Timeline Mode */}
      {showVAL && (
        <VALAssistant 
          user={user} 
          onClose={() => setShowVAL(false)}
          onMessageSent={loadData}
          mode="timeline"
        />
      )}

      {/* Notes Modal */}
      {notesModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-4 border-b border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                    <StickyNote className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-serif font-medium text-[#1C1917]">Add Note</h3>
                    <p className="text-xs text-[#57534E]">{notesModal.event?.title}</p>
                  </div>
                </div>
                <button 
                  onClick={closeNotesModal}
                  className="p-2 text-stone-400 hover:text-stone-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              <p className="text-sm text-[#57534E] mb-3">
                Add a personal reminder or important detail for this event.
              </p>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="e.g., Remind DJ to play our song, or Coordinate with photographer..."
                className="rounded-xl min-h-[120px] resize-none border-stone-200 focus:border-amber-300 focus:ring-amber-300"
                autoFocus
              />
              
              {/* Helper suggestions */}
              <div className="mt-4">
                <p className="text-xs text-[#A8A29E] mb-2">Quick suggestions:</p>
                <div className="flex flex-wrap gap-2">
                  {['Coordinate with photographer', 'Remind DJ', 'Check with caterer', 'Time buffer needed'].map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => setNoteText(noteText ? `${noteText}\n${suggestion}` : suggestion)}
                      className="text-xs bg-stone-100 text-[#57534E] px-3 py-1.5 rounded-full hover:bg-stone-200 transition-colors"
                    >
                      + {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-stone-50 border-t border-stone-100 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={closeNotesModal}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={saveNote}
                className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
              >
                <StickyNote size={16} className="mr-2" />
                Save Note
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timeline;
